import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitEarlyAccessForm, type EarlyAccessFormData } from '../../lib/formSubmissions';
import { trackLead, trackPartnerModalOpen } from '../../utils/analytics';

interface EarlyAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The partnership form ("Let's train your models."), styled to the lab
 * design. Rendered through a portal, so it re-declares the lab font/colors
 * itself. Submissions go through the existing Firestore pipeline.
 */
const INPUT_CLASS =
  'w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-[15px] text-lab-frost ' +
  'placeholder:text-lab-frost/35 transition-colors focus:border-lab-asr/70 focus:bg-white/[0.06]';

const LABEL_CLASS = 'mb-2 block font-plexmono text-[10.5px] uppercase tracking-[0.14em] text-lab-mist/90';

const EarlyAccessModal: React.FC<EarlyAccessModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState<EarlyAccessFormData>({
    name: '',
    email: '',
    company: '',
    useCase: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Escape closes; funnel event on open
  useEffect(() => {
    if (!isOpen) return;
    trackPartnerModalOpen();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await submitEarlyAccessForm({ ...formData, language: i18n.language });
      setSubmitStatus('success');
      trackLead(i18n.language);
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', useCase: '' });
        setSubmitStatus('idle');
        onClose();
      }, 2200);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : t('lab.modal.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-lab-night/80 p-4 font-instrument backdrop-blur-md"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 12 }}
          transition={{ duration: 0.25 }}
          className="relative max-h-[90dvh] w-full max-w-[560px] overflow-y-auto rounded-2xl border border-white/[0.14] bg-lab-ink/95 p-6 text-lab-frost shadow-[0_30px_90px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-9"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label={t('lab.modal.close')}
            className="absolute end-4 top-4 rounded-full border border-white/10 p-2 text-lab-frost/60 transition-colors hover:border-white/25 hover:text-lab-frost"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-6 pe-8">
            <div className="font-plexmono text-[10.5px] tracking-[0.18em] text-lab-asr">
              {t('lab.modal.kicker')}
            </div>
            <h3 className="mt-2.5 text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] sm:text-[30px]">
              {t('lab.modal.title')}
            </h3>
            <p className="mt-2.5 text-[14.5px] leading-[1.65] text-lab-frost/65">
              {t('lab.modal.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={LABEL_CLASS}>{t('lab.modal.nameLabel')}</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={INPUT_CLASS}
                  placeholder={t('lab.modal.namePlaceholder')}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>{t('lab.modal.emailLabel')}</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={INPUT_CLASS}
                  placeholder={t('lab.modal.emailPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS}>{t('lab.modal.companyLabel')}</label>
              <input
                type="text"
                name="company"
                required
                value={formData.company}
                onChange={handleInputChange}
                className={INPUT_CLASS}
                placeholder={t('lab.modal.companyPlaceholder')}
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>{t('lab.modal.useCaseLabel')}</label>
              <textarea
                name="useCase"
                required
                rows={3}
                value={formData.useCase}
                onChange={handleInputChange}
                className={`${INPUT_CLASS} resize-none`}
                placeholder={t('lab.modal.useCasePlaceholder')}
              />
            </div>

            {submitStatus === 'error' && (
              <div className="flex items-center gap-3 rounded-xl border border-red-400/30 bg-red-400/10 p-3.5 text-[13.5px] text-red-300">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {submitStatus === 'success' && (
              <div className="flex items-center gap-3 rounded-xl border border-lab-asr/30 bg-lab-asr/10 p-3.5 text-[13.5px] text-lab-asr">
                <Check className="h-4 w-4 flex-shrink-0" />
                <span>{t('lab.modal.success')}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || submitStatus === 'success'}
              className={`lab-siri w-full rounded-full py-3.5 text-[15px] font-semibold transition-all ${
                isSubmitting || submitStatus === 'success'
                  ? 'cursor-not-allowed bg-lab-frost/30 text-lab-ink/60'
                  : 'bg-lab-frost text-lab-ink hover:-translate-y-0.5'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2.5">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-lab-ink/25 border-t-lab-ink" />
                  {t('lab.modal.submitting')}
                </span>
              ) : submitStatus === 'success' ? (
                <span className="flex items-center justify-center gap-2">
                  <Check className="h-4 w-4" />
                  {t('lab.modal.submitted')}
                </span>
              ) : (
                t('lab.modal.submit')
              )}
            </button>
          </form>

          <p className="mt-5 text-center font-plexmono text-[10.5px] text-lab-frost/40">
            {t('lab.modal.privacyNote')}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EarlyAccessModal;
